import React from 'react';
import { createRoot } from 'react-dom/client';
import type { Poll, TrueTheme } from 'schema/poll';
import { createDefaultTheme } from 'schema/src/themes/createDefaultThemeValues';
import './crypto';
import { PollAppContexts } from './PollApp';
import './sentry';
import './style_modern.module.css';
import './style_old.module.css';
import {
  applyTuiOrTdsThemeHtmlAttribute,
  createStyleContent,
  getPollTemplateForTheme,
  getThemeStyleContent,
  getThemeVariablesStyleContent,
  injectThemeStyles,
} from './themes/themeUtils';
import { isWebview, shouldHideBorder } from './utils/location';
import { notifyParent } from './utils/notifyIframe';

let poll = window.__pollData;

if (!poll) {
  console.error(`no window.__pollData`);
} else {
  window.POLL_MIN_HEIGHT = poll.minHeight;

  const urlParams = new URLSearchParams(window.location.search);
  const title = urlParams.get('theme-title') ?? window.__title ?? null;
  const withoutBorder = shouldHideBorder();
  const theme = getTheme(poll);
  const template = resolvePollTemplate(poll.template);
  poll = resolvePollPixelList(poll);

  const isWebviewTemplate = poll.template === 'webview' || poll.template === 'dialog_webview';

  notifyParent({
    type: 'survey_iframe_init',
    theme,
  });

  if (isWebviewTemplate && !isWebview()) {
    removeRedundantMetaColorSchemeElement();
  }

  injectTheme(theme, template);

  if (!!poll.hideIframeBorder || withoutBorder) {
    hideIframeBorder();
  }

  const container = document.getElementById('root');

  if (container) {
    const root = createRoot(container);
    root.render(<PollAppContexts poll={{ ...poll, template }} theme={theme} title={title} />);
  } else {
    console.error('Root container not found');
  }
}

export function resolvePollTemplate(template: Poll['template']): Poll['template'] {
  try {
    const forceTemplate = localStorage.getItem('forceTemplate');

    if (forceTemplate) {
      return forceTemplate as Poll['template'];
    }
  } catch (error) {
    console.error(error);
  }

  const isUniversalPoll = template === 'webview' && !isWebview();

  if (isUniversalPoll) {
    return 'poll';
  }

  if (template === 'dialog_webview' && !isWebview()) {
    return 'dialog';
  }

  return template;
}

function injectTheme(theme: TrueTheme['data'] | undefined, template: Poll['template']) {
  if (!theme) return;

  const themeCustomVariablesStyleContent = getThemeVariablesStyleContent(template);
  const resolvedTemplate = getPollTemplateForTheme(template);

  applyTuiOrTdsThemeHtmlAttribute(theme, resolvedTemplate);

  injectThemeStyles(themeCustomVariablesStyleContent);

  const themeStyles = getThemeStyleContent(theme, resolvedTemplate);
  injectThemeStyles(themeStyles);
}

function removeRedundantMetaColorSchemeElement() {
  document.head.querySelector('[name="color-scheme"]')?.remove();
}

function getTheme(poll: Poll) {
  if (poll.theme?.data) {
    return poll.theme.data as TrueTheme['data'];
  }

  return createDefaultTheme();
}

function hideIframeBorder() {
  injectThemeStyles(createStyleContent(['--custom-tui-iframe-border: transparent'], ':root'));
}

function resolvePollPixelList(poll: Poll): Poll {
  const url = new URL(location.href);
  const suppressedPixels = url.searchParams.getAll('suppress-pixel[]');

  const pixels = poll.pixels.map(pixel => ({
    ...pixel,
    data: {
      ...pixel.data,
      events: pixel.data.events?.filter(event => !suppressedPixels.includes(event.name)),
    },
  }));

  return { ...poll, pixels };
}
